import { PzPublisher, PzTerm, PzUserSignUp } from 'models';
import axios, { AxiosError } from 'axios';
import { BffGenericErrorCode } from './server-error-codes';
import { Logger } from 'bernie-logger';
import AffiliateHubLogger from '../util/affiliate-hub-logger';
import { BFFAxiosResponse } from '../models/common';
import { SuccessResponse } from '../client/success-response';
import { FailureResponse } from '../client/failure-response';
import { PzUser } from '../models/pz';

export class PzSource {
  private logger: AffiliateHubLogger;

  private terms: PzTerm[];

  private pzLongLocales = [
    'de_at',
    'en_au',
    'en_ca',
    'en_us',
    'es_mx',
    'fr_ca',
    'ko_kr',
    'ms_my',
    'pt_br',
    'sv_se',
    'zh_cn',
    'zh_hk',
  ];

  public constructor(logger: Logger) {
    this.logger = new AffiliateHubLogger(logger);
    this.terms = [];
  }

  public async getPartnerizeTerms(locale: string, country: string) {
    const localeCountry = await this.checkLocaleCountry(locale, country);
    const terms = await this.loadPartnerizeTerms(localeCountry);

    return terms.partnerize;
  }

  public async getExpediaGroupTerms(locale: string, country: string) {
    const localeCountry = await this.checkLocaleCountry(locale, country);
    const terms = await this.loadPartnerizeTerms(localeCountry);

    return terms.expediagroup;
  }

  private async checkLocaleCountry(locale: string, country: string) {
    // TODO check country is in a valid list

    return [this.checkLocale(locale), country].join('/');
  }

  /**
   *
   * @param localeCountry
   * @private
   *
   * Converts the Bernie locale into one that PZ likes. Currently, the PZ locales are
   * ["bg", "cs", "da", "de", "de_at", "el", "en", "en_au", "en_ca", "en_us", "es", "es_mx",
   * "et", "fi", "fl", "fr", "fr_ca", "hu", "id", "it", "jp", "ko", "ko_kr", "lt", "lv",
   * "ms_my", "mt", "my", "nl", "no", "pl", "po", "pt", "pt_br", "ro", "ru", "sg", "sk", "sl",
   * "sv", "sv_se", "th", "tl", "tr", "vi", "zh_cn", "zh_hk"]
   *
   * We check if they're a valid "long" one, if not we lop off the end
   */

  private checkLocale(localeCountry: string) {
    const pzLocale = localeCountry.toLowerCase();

    return this.pzLongLocales.includes(pzLocale) ? pzLocale : pzLocale.split('_')[0];
  }

  public async getPartner(token: string): Promise<PzPublisher> {
    return await fetch(`/api/v1/pz/user/partner-id/${token}`, {
      method: 'GET',
    })
      .then((response) => response.json())
      .then((data) => {
        return data.user_accounts[0].publisher;
      });
  }

  private async loadPartnerizeTerms(localeCountry: string) {
    if (!this.terms[localeCountry]) {
      await fetch(`/api/v1/pz/reference/terms/${localeCountry}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
        .then((response) => {
          return response.json();
        })
        .then((localeData) => {
          const terms = {
            partnerize: {},
            expediagroup: {},
          };

          for (const termItem of localeData.terms) {
            switch (termItem.term.tnc_type) {
              case 'entity':
                terms.partnerize = termItem.term;
                break;
              case 'network':
                terms.expediagroup = termItem.term;
                break;
            }
          }

          // tslint:disable-next-line:no-console
          console.log('PZ T&Cs retrieved for ', localeCountry);

          this.terms[localeCountry] = terms;
        })
        .catch((error) => {
          // tslint:disable-next-line:no-console
          console.log('Could not retrieve PZ T&Cs for ', localeCountry, error);
        });
    } else {
      // tslint:disable-next-line:no-console
      console.log('CACHED PZ T&Cs retrieved for ', localeCountry);
    }

    return this.terms[localeCountry];
  }

  public async createUser(programName: string, user: PzUserSignUp) {
    let resOk = false;
    let resStatusText = null;

    // Create the user + partner on PZ
    return await fetch('/api/v1/pz/user/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user }),
    })
      .then((response) => {
        resOk = response.ok;
        resStatusText = response.statusText;

        return response.json();
      })
      .then((createUserData) => {
        if (createUserData.success === false) {
          return createUserData;
        } else {
          const partnerId = createUserData.partnerId;

          return this.assignCampaign(programName, partnerId, user.campaignTermsId);
        }
      });
  }

  public async assignCampaign(programName: string, partnerId: string, campaignTermsId: string) {
    const campaignRequest = {
      programName,
      campaignTermsId,
    };

    // Assign the user to the requested campaign on PZ
    return fetch(`/api/v1/pz/user/${partnerId}/campaign`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(campaignRequest),
    })
      .then((campaignResponse) => {
        return campaignResponse.json();
      })
      .then((assignCampaignData) => {
        return {
          success: true,
          error: null,
          body: assignCampaignData,
        };
      })
      .catch((error) => {
        //logger.warn("FAILED_TO_ASSIGN_CAMPAIGN", {error})
        return {
          success: false,
          error,
          body: null,
        };
      });
  }

  public async assignCampaignAsPartner(
    programName: string,
    partnerId: string,
    campaignTermsId: string,
    accessToken: string,
  ) {
    const campaignRequest = {
      programName,
      campaignTermsId,
      accessToken,
    };

    // Assign the user to the requested campaign on PZ
    return fetch(`/api/v1/pz/user/${partnerId}/campaign/external`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(campaignRequest),
    })
      .then((campaignResponse) => {
        return campaignResponse.json();
      })
      .then((assignCampaignData) => {
        return {
          success: true,
          error: null,
          body: assignCampaignData,
        };
      })
      .catch((error) => {
        this.logger.warn('FAILED_TO_ASSIGN_CAMPAIGN', { error });

        return {
          success: false,
          error,
          body: null,
        };
      });
  }

  public async assignBulkCampaignsAsBrand(
    programs: string[],
    partnerId: string,
    userId: string,
    locale: string,
  ): Promise<BFFAxiosResponse> {
    try {
      const res = await axios.post<BFFAxiosResponse>(
        `/api/v1/pz/user/${partnerId}/bulkCampaigns`,
        {
          programs,
          userId,
          userLocale: locale,
        },
        { timeout: 15000 },
      );

      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { data: errorBody, status } = (error as AxiosError).response;

        return {
          success: false,
          errorCode: status.toString(),
          body: errorBody.errorMessage,
        };
      }

      return {
        success: false,
        errorCode: '500',
        body: { message: (error as Error).message },
      };
    }
  }

  public async getPublisher(publisherId: string): Promise<SuccessResponse<PzUser>> {
    try {
      const response = await axios.get(`/api/v1/pz/user/${publisherId}`);

      if (response.data.success) {
        return Promise.resolve(new SuccessResponse(response.data.body));
      }

      return Promise.reject(response.data);
    } catch (ex) {
      this.logger.error('INVALID_PZ_PUBLISHER_REQUESTED', ex);

      if (ex.response?.data?.success !== undefined) {
        return Promise.reject(ex.response.data);
      }

      // Maybe should be a specific error
      return Promise.reject(new FailureResponse(BffGenericErrorCode.UNKNOWN, ex.response));
    }
  }
}
